
import { useState, useEffect } from "react";
import {
  collection, addDoc, updateDoc, deleteDoc,
  doc, onSnapshot, orderBy, query,
  serverTimestamp, getDocs, getDoc, setDoc
} from "firebase/firestore";
import { db, auth } from "./firebase";
import emailjs from "@emailjs/browser";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
// Splash redirect — first-time visitors only
if (!document.cookie.includes('splash_seen=1')) {
  document.cookie = 'splash_seen=1;path=/;max-age=86400';
  window.location.replace('/splash.html');
}
const C = {
  bg:"#F0E9D6", card:"#FAF6ED", white:"#FEFCF5",
  rose:"#B06B56", sage:"#5A8B5E", mustard:"#A87E28",
  slate:"#4A7496", plum:"#72568A", teal:"#3D7A72",
  border:"#C5B8A4", text:"#201C15", muted:"#7A6D5C", light:"#E2D8C4",
};
const CATS = {
  Writing:     { color:C.rose,    icon:"✎" },
  Photography: { color:C.slate,   icon:"◈" },
  Sports:      { color:C.sage,    icon:"◆" },
  Music:       { color:C.mustard, icon:"♪" },
  "App Dev":   { color:C.plum,    icon:"⊞" },
  YouTube:     { color:C.teal,    icon:"▶" },
};

const ALL_CATS = ["All", ...Object.keys(CATS)];
const fmt = () => {
  const d = new Date();
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,"0")}.${String(d.getDate()).padStart(2,"0")}`;
};

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^&?/\s]{11})/);
  return m ? m[1] : null;
}

const Badge = ({ cat, sm }) => {
  const m = CATS[cat] || { color:C.muted, icon:"•" };
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4,
      fontSize:sm?9:10, fontWeight:700, fontFamily:"monospace",
      color:m.color, letterSpacing:1,
      border:`1.5px solid ${m.color}`, borderRadius:2,
      padding:sm?"2px 6px":"3px 9px" }}>
      {m.icon} {cat.toUpperCase()}
    </span>
  );
};
const Inp = ({ style={}, ...p }) => (
  <input style={{ background:C.white, border:`1.5px solid ${C.border}`,
    borderRadius:2, padding:"9px 12px", fontSize:13, color:C.text,
    outline:"none", fontFamily:"inherit", width:"100%",
    boxSizing:"border-box", ...style }} {...p} />
);
const Btn = ({ children, style={}, ...p }) => (
  <button style={{ cursor:"pointer", fontFamily:"monospace", fontWeight:700,
    fontSize:11, letterSpacing:1, borderRadius:2,
    padding:"9px 18px", ...style }} {...p}>
    {children}
  </button>
);

// ── Media Display ─────────────────────────────────────────────
const MediaDisplay = ({ post }) => {
  const [slide, setSlide] = useState(0);
  const imgs = post.images || [];
  const ytId = getYouTubeId(post.youtubeUrl);
  if (!ytId && imgs.length === 0 && !post.videoUrl && !post.appPreviewUrl) return null;
  return (
    <div style={{ borderBottom:`1px dashed ${C.light}`, paddingBottom:16, marginBottom:16 }}>
      {ytId && (
        <div style={{ marginBottom:12, borderRadius:4, overflow:"hidden", position:"relative", paddingTop:"56.25%" }}>
          <iframe src={`https://www.youtube.com/embed/${ytId}`}
            style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", border:"none" }}
            allowFullScreen title="YouTube video" />
        </div>
      )}
      {imgs.length > 0 && (
        <div style={{ marginBottom:12 }}>
          <div style={{ position:"relative", borderRadius:4, overflow:"hidden", background:"#000" }}>
            <img src={imgs[slide]} alt={`slide ${slide+1}`}
              style={{ width:"100%", maxHeight:400, objectFit:"cover", display:"block" }}
              onError={e=>e.target.style.display="none"} />
            {imgs.length > 1 && (
              <>
                <button onClick={()=>setSlide(s=>(s-1+imgs.length)%imgs.length)}
                  style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)",
                    background:"rgba(0,0,0,0.5)", border:"none", color:"#fff",
                    borderRadius:"50%", width:32, height:32, cursor:"pointer", fontSize:16 }}>‹</button>
                <button onClick={()=>setSlide(s=>(s+1)%imgs.length)}
                  style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)",
                    background:"rgba(0,0,0,0.5)", border:"none", color:"#fff",
                    borderRadius:"50%", width:32, height:32, cursor:"pointer", fontSize:16 }}>›</button>
                <div style={{ position:"absolute", bottom:8, left:"50%", transform:"translateX(-50%)", display:"flex", gap:4 }}>
                  {imgs.map((_,i)=>(
                    <div key={i} onClick={()=>setSlide(i)}
                      style={{ width:8, height:8, borderRadius:"50%", cursor:"pointer",
                        background: i===slide?"#fff":"rgba(255,255,255,0.5)" }} />
                  ))}
                </div>
              </>
            )}
          </div>
          {imgs.length > 1 && <div style={{ fontSize:10, fontFamily:"monospace", color:C.muted, textAlign:"center", marginTop:4 }}>{slide+1} / {imgs.length}</div>}
        </div>
      )}
      {post.videoUrl && !ytId && (
        <div style={{ marginBottom:12 }}>
          <video controls style={{ width:"100%", borderRadius:4, maxHeight:360 }}>
            <source src={post.videoUrl} />
          </video>
        </div>
      )}
      {post.appPreviewUrl && (
        <div style={{ marginBottom:12, textAlign:"center" }}>
          <img src={post.appPreviewUrl} alt="App preview"
            style={{ maxWidth:300, width:"100%", borderRadius:8, border:`1.5px solid ${C.border}`, display:"inline-block" }}
            onError={e=>e.target.style.display="none"} />
          {post.appStoreUrl && (
            <div style={{ marginTop:8 }}>
              <a href={post.appStoreUrl} target="_blank" rel="noreferrer"
                style={{ fontSize:11, fontFamily:"monospace", color:C.slate, textDecoration:"underline" }}>
                🔗 View App →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Post Card ─────────────────────────────────────────────────
const PostCard = ({ post, onClick, isAdmin, onEdit, onDelete }) => {
  const [hov, setHov] = useState(false);
  const m = CATS[post.cat] || { color:C.muted };
  const imgs = post.images || [];
  const ytId = getYouTubeId(post.youtubeUrl);
  const thumb = imgs[0] || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null) || post.appPreviewUrl;
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ background:C.card, border:`2px solid ${hov?m.color:C.border}`,
        borderRadius:3, overflow:"hidden", transition:"all 0.15s",
        transform:hov?"translateY(-2px)":"none", position:"relative" }}>
      <div style={{ position:"absolute", top:0, right:0, width:18, height:18,
        borderLeft:`2px solid ${m.color}`, borderBottom:`2px solid ${m.color}`,
        opacity:hov?1:0.25, transition:"opacity 0.15s" }} />
      {thumb && (
        <div style={{ height:160, overflow:"hidden", background:"#eee" }}>
          <img src={thumb} alt="thumbnail"
            style={{ width:"100%", height:"100%", objectFit:"cover" }}
            onError={e=>e.target.parentElement.style.display="none"} />
        </div>
      )}
      <div onClick={()=>onClick(post)} style={{ cursor:"pointer", padding:20, display:"flex", flexDirection:"column", gap:10 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <Badge cat={post.cat} />
          <span style={{ fontSize:9, fontFamily:"monospace", color:C.muted }}>{post.date}</span>
        </div>
        <div style={{ fontSize:15, fontWeight:700, color:C.text, lineHeight:1.3 }}>{post.title}</div>
        <div style={{ fontSize:12, color:C.muted, lineHeight:1.65 }}>{post.excerpt}</div>
        <div style={{ display:"flex", gap:6 }}>
          {ytId && <span style={{ fontSize:10, color:C.rose }}>▶ YouTube</span>}
          {imgs.length > 0 && <span style={{ fontSize:10, color:C.slate }}>◈ {imgs.length} photo{imgs.length>1?"s":""}</span>}
          {post.appPreviewUrl && <span style={{ fontSize:10, color:C.plum }}>⊞ App</span>}
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
          paddingTop:8, borderTop:`1px dashed ${C.light}` }}>
          <span style={{ fontSize:10, fontFamily:"monospace", color:m.color }}>♥ {post.likes||0}</span>
          <span style={{ fontSize:10, fontFamily:"monospace", color:C.muted }}>READ →</span>
        </div>
      </div>
      {isAdmin && (
        <div style={{ display:"flex", gap:6, margin:"0 20px 16px" }}>
          <Btn onClick={e=>{e.stopPropagation();onEdit(post)}}
            style={{ flex:1, padding:"5px 0", background:"none", border:`1.5px solid ${C.border}`, color:C.muted, fontSize:9 }}>EDIT</Btn>
          <Btn onClick={e=>{e.stopPropagation();onDelete(post.id)}}
            style={{ flex:1, padding:"5px 0", background:"none", border:`1.5px solid ${C.rose}`, color:C.rose, fontSize:9 }}>DELETE</Btn>
        </div>
      )}
    </div>
  );
};

// ── Post Modal ────────────────────────────────────────────────
const PostModal = ({ post, onClose, isAdmin }) => {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [liked, setLiked] = useState(false);
  const [cnt, setCnt] = useState(post.likes||0);
  const [cmts, setCmts] = useState([]);
  const [loading, setLoading] = useState(true);
  const m = CATS[post.cat] || { color:C.muted, icon:"•" };
  useEffect(() => {
    const q = query(collection(db,"posts",post.id,"comments"), orderBy("createdAt","asc"));
    return onSnapshot(q, snap => { setCmts(snap.docs.map(d=>({id:d.id,...d.data()}))); setLoading(false); });
  }, [post.id]);
  useEffect(() => {
    const h = e => { if(e.key==="Escape") onClose(); };
    window.addEventListener("keydown",h);
    return () => window.removeEventListener("keydown",h);
  }, [onClose]);
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(18,14,8,0.83)", zIndex:900,
      display:"flex", alignItems:"flex-start", justifyContent:"center",
      overflowY:"auto", padding:"28px 16px" }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:C.card,
        border:`2px solid ${m.color}`, borderRadius:3, maxWidth:640, width:"100%", marginBottom:28 }}>
        <div style={{ padding:"22px 24px 18px", borderBottom:`1px dashed ${C.light}`, position:"relative" }}>
          <button onClick={onClose} style={{ position:"absolute", top:14, right:18,
            background:"none", border:"none", fontSize:20, cursor:"pointer", color:C.muted }}>✕</button>
          <Badge cat={post.cat} />
          <div style={{ fontSize:22, fontWeight:700, color:C.text, lineHeight:1.25, margin:"10px 0 8px" }}>{post.title}</div>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <span style={{ fontSize:10, fontFamily:"monospace", color:C.muted }}>{post.date}</span>
            <button onClick={async()=>{ const n=liked?cnt-1:cnt+1; setLiked(!liked); setCnt(n); await updateDoc(doc(db,"posts",post.id),{likes:n}); }}
              style={{ background:"none", border:"none", cursor:"pointer", color:liked?m.color:C.muted, fontFamily:"monospace", fontSize:12, fontWeight:700, padding:0 }}>
              {liked?"♥":"♡"} {cnt}
            </button>
          </div>
        </div>
        <div style={{ padding:"16px 24px 0" }}><MediaDisplay post={post} /></div>
        <div style={{ padding:"4px 24px 20px", borderBottom:`1px dashed ${C.light}` }}>
          {(post.content||"").split("\n\n").map((p,i)=>(
            <p key={i} style={{ fontSize:14, color:C.text, lineHeight:1.9, margin:"0 0 14px", fontFamily:"Georgia,serif" }}>{p}</p>
          ))}
        </div>
        <div style={{ padding:"20px 24px" }}>
          <div style={{ fontSize:9, fontWeight:700, fontFamily:"monospace", color:C.muted, letterSpacing:3, marginBottom:14 }}>COMMENTS ({cmts.length})</div>
          {loading ? <div style={{ fontSize:11, color:C.muted, marginBottom:14 }}>Loading…</div>
            : cmts.length===0 ? <div style={{ fontSize:12, color:C.muted, fontStyle:"italic", marginBottom:16 }}>No comments yet. Be the first.</div>
            : <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
                {cmts.map(cm=>(
                  <div key={cm.id} style={{ background:C.bg, border:`1.5px solid ${C.border}`, borderRadius:2, padding:"10px 13px", position:"relative" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                      <span style={{ fontSize:11, fontWeight:700, fontFamily:"monospace", color:m.color }}>{cm.name}</span>
                      <span style={{ fontSize:9, fontFamily:"monospace", color:C.muted }}>{cm.date}</span>
                    </div>
                    <div style={{ fontSize:13, color:C.text, lineHeight:1.65 }}>{cm.text}</div>
                    {isAdmin && <button onClick={()=>deleteDoc(doc(db,"posts",post.id,"comments",cm.id))}
                      style={{ position:"absolute", bottom:8, right:10, background:"none", border:"none", fontSize:9, fontFamily:"monospace", color:C.rose, cursor:"pointer" }}>DELETE</button>}
                  </div>
                ))}
              </div>
          }
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <Inp value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" style={{ fontFamily:"monospace", fontSize:12 }} />
            <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Leave a comment…" rows={3}
              style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:2, padding:"9px 12px", fontSize:13, color:C.text, outline:"none", resize:"vertical", lineHeight:1.65, fontFamily:"inherit", width:"100%", boxSizing:"border-box" }} />
            <Btn onClick={async()=>{ if(!name.trim()||!text.trim()) return; await addDoc(collection(db,"posts",post.id,"comments"),{ name:name.trim(), text:text.trim(), date:fmt(), createdAt:serverTimestamp() }); setText(""); }}
              style={{ background:m.color, border:"none", color:"#fff", alignSelf:"flex-end", padding:"8px 18px" }}>
              POST COMMENT →
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Admin Panel ───────────────────────────────────────────────
const AdminPanel = ({ onClose, onSave, editPost }) => {
  const blank = { cat:"Writing", title:"", excerpt:"", content:"", youtubeUrl:"", images:[""], videoUrl:"", appPreviewUrl:"", appStoreUrl:"" };
  const [form, setForm] = useState(editPost ? {
    cat:editPost.cat, title:editPost.title, excerpt:editPost.excerpt,
    content:editPost.content||"", youtubeUrl:editPost.youtubeUrl||"",
    images:editPost.images&&editPost.images.length>0?editPost.images:[""],
    videoUrl:editPost.videoUrl||"", appPreviewUrl:editPost.appPreviewUrl||"", appStoreUrl:editPost.appStoreUrl||""
  } : blank);
  const [prev, setPrev] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const setImage = (i,v) => { const imgs=[...form.images]; imgs[i]=v; set("images",imgs); };
  const save = async () => {
    if(!form.title.trim()||!form.excerpt.trim()) return;
    setBusy(true);
    await onSave({...form, images:form.images.filter(u=>u.trim()!=="")}, editPost?.id);
    setSaved(true); setBusy(false);
    setTimeout(()=>{ setSaved(false); if(!editPost) setForm(blank); }, 1600);
  };
  const L = { fontSize:9, fontWeight:700, fontFamily:"monospace", letterSpacing:2, color:C.muted, display:"block", marginBottom:5 };
  const IS = { background:C.white, border:`1.5px solid ${C.border}`, borderRadius:2, padding:"9px 12px", fontSize:13, color:C.text, outline:"none", boxSizing:"border-box", width:"100%", fontFamily:"inherit" };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(18,14,8,0.88)", zIndex:950,
      display:"flex", alignItems:"stretch", justifyContent:"flex-end" }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ width:"min(520px,100%)", background:C.card,
        borderLeft:`3px solid ${C.rose}`, display:"flex", flexDirection:"column", overflowY:"auto" }}>
        <div style={{ padding:"16px 22px", borderBottom:`1px dashed ${C.light}`, position:"sticky",
          top:0, background:C.card, zIndex:2, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:8, fontFamily:"monospace", color:C.rose, fontWeight:700, letterSpacing:3 }}>ADMIN PANEL</div>
            <div style={{ fontSize:16, fontWeight:700, fontFamily:"monospace", color:C.text }}>{editPost?"Edit Post":"New Post"}</div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={()=>setPrev(!prev)} style={{ background:"none", border:`1.5px solid ${C.border}`, color:C.muted, padding:"5px 12px", fontSize:9 }}>{prev?"← EDIT":"PREVIEW"}</Btn>
            <button onClick={onClose} style={{ background:"none", border:"none", color:C.muted, fontSize:20, cursor:"pointer" }}>✕</button>
          </div>
        </div>
        <div style={{ padding:22, flex:1 }}>
          {prev ? (
            <div>
              <Badge cat={form.cat} />
              <div style={{ fontSize:20, fontWeight:700, color:C.text, margin:"12px 0 10px" }}>{form.title||"No title"}</div>
              <MediaDisplay post={form} />
              <div style={{ fontSize:13, color:C.muted, lineHeight:1.7 }}>{form.excerpt}</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              <div>
                <span style={L}>CATEGORY</span>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {Object.entries(CATS).map(([cat,cm])=>(
                    <Btn key={cat} onClick={()=>set("cat",cat)}
                      style={{ padding:"5px 10px", background:form.cat===cat?cm.color:"none",
                        border:`1.5px solid ${cm.color}`, color:form.cat===cat?"#fff":cm.color, fontSize:9 }}>
                      {cm.icon} {cat}
                    </Btn>
                  ))}
                </div>
              </div>
              <div><span style={L}>TITLE</span><input value={form.title} onChange={e=>set("title",e.target.value)} placeholder="Post title…" style={{ ...IS, fontWeight:700, fontSize:15 }} /></div>
              <div><span style={L}>EXCERPT</span><textarea value={form.excerpt} onChange={e=>set("excerpt",e.target.value)} placeholder="1–2 sentences…" rows={2} style={{ ...IS, resize:"vertical" }} /></div>
              <div><span style={L}>FULL CONTENT</span><textarea value={form.content} onChange={e=>set("content",e.target.value)} placeholder="Full post. Blank lines = paragraphs." rows={5} style={{ ...IS, resize:"vertical" }} /></div>
              <div style={{ borderTop:`1px dashed ${C.light}`, paddingTop:16 }}>
                <div style={{ fontSize:10, fontWeight:700, fontFamily:"monospace", color:C.mustard, letterSpacing:2, marginBottom:12 }}>📎 MEDIA</div>
                <div style={{ marginBottom:12 }}><span style={L}>▶ YOUTUBE URL</span><input value={form.youtubeUrl} onChange={e=>set("youtubeUrl",e.target.value)} placeholder="https://youtube.com/watch?v=..." style={IS} /></div>
                <div style={{ marginBottom:12 }}>
                  <span style={L}>◈ IMAGE URLs</span>
                  {form.images.map((img,i)=>(
                    <div key={i} style={{ display:"flex", gap:6, marginBottom:6 }}>
                      <input value={img} onChange={e=>setImage(i,e.target.value)} placeholder={`Image URL ${i+1}…`} style={{ ...IS, flex:1 }} />
                      {form.images.length > 1 && <button onClick={()=>set("images",form.images.filter((_,idx)=>idx!==i))}
                        style={{ background:"none", border:`1.5px solid ${C.rose}`, borderRadius:2, color:C.rose, cursor:"pointer", padding:"0 8px" }}>✕</button>}
                    </div>
                  ))}
                  <Btn onClick={()=>set("images",[...form.images,""])} style={{ background:"none", border:`1.5px solid ${C.border}`, color:C.muted, padding:"5px 12px", fontSize:9, marginTop:4 }}>+ ADD IMAGE</Btn>
                </div>
                <div style={{ marginBottom:12 }}><span style={L}>🎬 VIDEO URL</span><input value={form.videoUrl} onChange={e=>set("videoUrl",e.target.value)} placeholder="https://example.com/video.mp4" style={IS} /></div>
                <div style={{ marginBottom:12 }}><span style={L}>⊞ APP PREVIEW IMAGE URL</span><input value={form.appPreviewUrl} onChange={e=>set("appPreviewUrl",e.target.value)} placeholder="https://example.com/screenshot.png" style={IS} /></div>
                <div><span style={L}>🔗 APP STORE LINK</span><input value={form.appStoreUrl} onChange={e=>set("appStoreUrl",e.target.value)} placeholder="https://apps.apple.com/..." style={IS} /></div>
              </div>
            </div>
          )}
        </div>
        <div style={{ padding:"14px 22px", borderTop:`1px dashed ${C.light}`, position:"sticky", bottom:0, background:C.card }}>
          {saved ? <div style={{ textAlign:"center", padding:12, border:`1.5px solid ${C.sage}`, borderRadius:2, color:C.sage, fontFamily:"monospace", fontSize:12, fontWeight:700 }}>✓ {editPost?"UPDATED":"PUBLISHED"}</div>
            : <div style={{ display:"flex", gap:8 }}>
                {!editPost && <Btn onClick={()=>setForm(blank)} style={{ flex:1, background:"none", border:`1.5px solid ${C.border}`, color:C.muted, padding:"10px 0", fontSize:9 }}>RESET</Btn>}
                <Btn onClick={save} disabled={busy} style={{ flex:2, background:busy?C.muted:C.rose, border:"none", color:"#fff", padding:"10px 0", fontSize:12 }}>
                  {busy?"SAVING…":editPost?"UPDATE →":"PUBLISH →"}
                </Btn>
              </div>
          }
        </div>
      </div>
    </div>
  );
};

// ── Dashboard ─────────────────────────────────────────────────
const Dashboard = ({ onClose, posts }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("stats");
  const [selSubs, setSelSubs] = useState([]);
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [msgSent, setMsgSent] = useState(false);

  useEffect(() => {
    const q = query(collection(db,"subscribers"), orderBy("subscribedAt","desc"));
    return onSnapshot(q, snap => {
      setSubscribers(snap.docs.map(d=>({id:d.id,...d.data()})));
      setLoading(false);
    });
  }, []);

  const delSub = async id => { await deleteDoc(doc(db,"subscribers",id)); };

  const toggleSel = id => setSelSubs(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);
  const selAll = () => setSelSubs(subscribers.map(s=>s.id));
  const selNone = () => setSelSubs([]);

const sendMsg = async () => {
    if (!msgSubject.trim() || !msgBody.trim()) return;
    const targets = selSubs.length > 0 ? subscribers.filter(s=>selSubs.includes(s.id)) : subscribers;
    if (targets.length === 0) return;
    for (const sub of targets) {
      // 1) keep a record in Firebase
      await addDoc(collection(db,"messages"), {
        to: sub.email,
        subject: msgSubject,
        body: msgBody,
        sentAt: serverTimestamp()
      });
      // 2) actually send the email via EmailJS
      try {
        await emailjs.send(
          "service_y0l0cic",
          "template_rlt5n4h",
          { to_email: sub.email, subject: msgSubject, message: msgBody },
          { publicKey: "WdtyFTSFGqnsdX8uB" }
        );
      } catch (e) {
        console.error("Email send failed for", sub.email, e);
      }
    }
    setMsgSent(true);
    setTimeout(()=>{ setMsgSent(false); setMsgSubject(""); setMsgBody(""); setSelSubs([]); }, 2000);
  };

  const totalComments = posts.reduce((a,p)=>a+(p.commentCount||0),0);
  const totalLikes = posts.reduce((a,p)=>a+(p.likes||0),0);

  const L = { fontSize:9, fontWeight:700, fontFamily:"monospace", letterSpacing:2, color:C.muted, display:"block", marginBottom:5 };
  const IS = { background:C.white, border:`1.5px solid ${C.border}`, borderRadius:2, padding:"9px 12px", fontSize:13, color:C.text, outline:"none", boxSizing:"border-box", width:"100%", fontFamily:"inherit" };

  const TabBtn = ({id,label}) => (
    <button onClick={()=>setTab(id)} style={{ background:"none", border:"none", cursor:"pointer",
      padding:"8px 16px", fontSize:10, fontWeight:700, fontFamily:"monospace", letterSpacing:1,
      color:tab===id?C.text:C.muted,
      borderBottom:tab===id?`2px solid ${C.rose}`:"2px solid transparent" }}>{label}</button>
  );

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(18,14,8,0.88)", zIndex:950,
      display:"flex", alignItems:"stretch", justifyContent:"flex-end" }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ width:"min(580px,100%)", background:C.card,
        borderLeft:`3px solid ${C.mustard}`, display:"flex", flexDirection:"column" }}>

        {/* Header */}
        <div style={{ padding:"16px 22px", borderBottom:`1px dashed ${C.light}`,
          display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <div>
            <div style={{ fontSize:8, fontFamily:"monospace", color:C.mustard, fontWeight:700, letterSpacing:3 }}>ADMIN</div>
            <div style={{ fontSize:16, fontWeight:700, fontFamily:"monospace", color:C.text }}>Dashboard</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.muted, fontSize:20, cursor:"pointer" }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px dashed ${C.light}`, flexShrink:0 }}>
          <TabBtn id="stats" label="📊 STATS" />
          <TabBtn id="subscribers" label={`📬 SUBSCRIBERS (${subscribers.length})`} />
          <TabBtn id="message" label="✉️ MESSAGE" />
          <TabBtn id="settings" label="⚙️ SETTINGS" />
        </div>

        {/* Content */}
        <div style={{ padding:22, flex:1, overflowY:"auto" }}>

          {/* Stats */}
          {tab==="stats" && (
            <div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12, marginBottom:24 }}>
                {[
                  { label:"Total Posts", value:posts.length, color:C.rose },
                  { label:"Subscribers", value:subscribers.length, color:C.mustard },
                  { label:"Total Likes", value:totalLikes, color:C.sage },
                  { label:"Categories", value:Object.keys(CATS).length, color:C.slate },
                ].map(s=>(
                  <div key={s.label} style={{ background:C.white, border:`2px solid ${C.border}`, borderRadius:3, padding:"16px 18px" }}>
                    <div style={{ fontSize:28, fontWeight:900, fontFamily:"monospace", color:s.color }}>{s.value}</div>
                    <div style={{ fontSize:10, fontFamily:"monospace", color:C.muted, letterSpacing:1 }}>{s.label.toUpperCase()}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:10, fontWeight:700, fontFamily:"monospace", color:C.muted, letterSpacing:3, marginBottom:12 }}>POSTS BY CATEGORY</div>
              {Object.entries(CATS).map(([cat,m])=>{
                const count = posts.filter(p=>p.cat===cat).length;
                const pct = posts.length > 0 ? Math.round(count/posts.length*100) : 0;
                return (
                  <div key={cat} style={{ marginBottom:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:11, fontFamily:"monospace", color:m.color }}>{m.icon} {cat}</span>
                      <span style={{ fontSize:11, fontFamily:"monospace", color:C.muted }}>{count} posts</span>
                    </div>
                    <div style={{ height:6, background:C.light, borderRadius:3 }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:m.color, borderRadius:3, transition:"width 0.3s" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Subscribers */}
          {tab==="subscribers" && (
            <div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                <div style={{ fontSize:12, color:C.muted, fontFamily:"monospace" }}>
                  {subscribers.length} subscriber{subscribers.length!==1?"s":""}
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <Btn onClick={selAll} style={{ padding:"4px 10px", background:"none", border:`1.5px solid ${C.border}`, color:C.muted, fontSize:9 }}>SELECT ALL</Btn>
                  <Btn onClick={selNone} style={{ padding:"4px 10px", background:"none", border:`1.5px solid ${C.border}`, color:C.muted, fontSize:9 }}>NONE</Btn>
                </div>
              </div>
              {loading ? <div style={{ fontFamily:"monospace", fontSize:12, color:C.muted }}>Loading…</div>
                : subscribers.length===0
                  ? <div style={{ textAlign:"center", padding:"32px 0", color:C.muted, fontFamily:"monospace", fontSize:12 }}>No subscribers yet.</div>
                  : <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {subscribers.map(sub=>(
                        <div key={sub.id} style={{ display:"flex", alignItems:"center", gap:10,
                          background:C.white, border:`1.5px solid ${selSubs.includes(sub.id)?C.mustard:C.border}`,
                          borderRadius:2, padding:"10px 14px" }}>
                          <input type="checkbox" checked={selSubs.includes(sub.id)}
                            onChange={()=>toggleSel(sub.id)} style={{ cursor:"pointer" }} />
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{sub.email}</div>
                            <div style={{ fontSize:10, fontFamily:"monospace", color:C.muted }}>{sub.date}</div>
                          </div>
                          <button onClick={()=>delSub(sub.id)}
                            style={{ background:"none", border:"none", color:C.rose, cursor:"pointer", fontSize:16, lineHeight:1 }}>✕</button>
                        </div>
                      ))}
                    </div>
              }
              {selSubs.length > 0 && (
                <div style={{ marginTop:12, padding:"10px 14px", background:"rgba(160,126,40,0.08)",
                  border:`1.5px solid ${C.mustard}`, borderRadius:2, fontSize:11, fontFamily:"monospace", color:C.mustard }}>
                  {selSubs.length} selected — go to ✉️ MESSAGE tab to send email
                </div>
              )}
            </div>
          )}

          {/* Message */}
          {tab==="message" && (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div style={{ padding:"12px 16px", background:"rgba(74,116,150,0.08)",
                border:`1.5px solid ${C.slate}`, borderRadius:2, fontSize:12, color:C.slate, fontFamily:"monospace" }}>
                {selSubs.length > 0
                  ? `📬 Sending to ${selSubs.length} selected subscriber${selSubs.length!==1?"s":""}`
                  : `📬 Sending to all ${subscribers.length} subscriber${subscribers.length!==1?"s":""}`}
              </div>
              <div><span style={L}>SUBJECT</span><input value={msgSubject} onChange={e=>setMsgSubject(e.target.value)} placeholder="New post: Title…" style={IS} /></div>
              <div><span style={L}>MESSAGE</span>
                <textarea value={msgBody} onChange={e=>setMsgBody(e.target.value)}
                  placeholder="Hey! A new post is up on Rubenology™. Check it out at rabbitonly.com…" rows={8}
                  style={{ ...IS, resize:"vertical", lineHeight:1.8 }} />
              </div>
              <div style={{ padding:"10px 14px", background:C.bg, border:`1px dashed ${C.border}`, borderRadius:2, fontSize:11, color:C.muted, fontFamily:"monospace" }}>
                ⚠️ Note: To actually send emails, connect EmailJS or a similar service. Messages are currently saved to Firebase only.
              </div>
              {msgSent
                ? <div style={{ textAlign:"center", padding:12, border:`1.5px solid ${C.sage}`, borderRadius:2, color:C.sage, fontFamily:"monospace", fontSize:12, fontWeight:700 }}>✓ Messages saved!</div>
                : <Btn onClick={sendMsg} style={{ background:C.mustard, border:"none", color:"#fff", padding:"12px 0", fontSize:12 }}>
                    SEND MESSAGE →
                  </Btn>
              }
           </div>
          )}
          {/* Settings */}
          {tab==="settings" && (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              <div style={{ fontSize:10, fontWeight:700, fontFamily:"monospace", color:C.muted, letterSpacing:3, marginBottom:4 }}>CHANGE PASSWORD</div>
              <div style={{ padding:"14px 16px", background:"rgba(176,107,86,0.08)", border:`1.5px solid ${C.rose}`, borderRadius:2, fontSize:12, color:C.muted, fontFamily:"monospace" }}>
                ⚠️ Password is stored in App.js. Change it in VS Code line 22, then rebuild and upload.
              </div>
              <div style={{ fontSize:10, fontFamily:"monospace", color:C.muted, letterSpacing:1 }}>CURRENT PASSWORD LOCATION</div>
              <div style={{ padding:"12px 16px", background:C.white, border:`1.5px solid ${C.border}`, borderRadius:2, fontFamily:"monospace", fontSize:13, color:C.text }}>
                📁 src/App.js — Line 22
              </div>
              <div style={{ fontSize:10, fontWeight:700, fontFamily:"monospace", color:C.muted, letterSpacing:3, marginTop:8 }}>SITE INFO</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {[
                  { label:"Domain", value:"rabbitonly.com" },
                  { label:"Firebase", value:"rubenology-7179a" },
                  { label:"Posts", value:posts.length },
                ].map(item=>(
                  <div key={item.label} style={{ padding:"10px 14px", background:C.white, border:`1.5px solid ${C.border}`, borderRadius:2 }}>
                    <div style={{ fontSize:9, fontFamily:"monospace", color:C.muted, letterSpacing:1, marginBottom:4 }}>{item.label.toUpperCase()}</div>
                    <div style={{ fontSize:13, fontWeight:700, fontFamily:"monospace", color:C.text }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          )}
        </div>
      </div>
    </div>
  );
};

// ── Login Modal ───────────────────────────────────────────────
const LoginModal = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const attempt = async () => {
    if (!email.trim() || !pw || busy) return;
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), pw);
      onClose();
    } catch (e) {
      setErr(true); setTimeout(()=>setErr(false),2000); setPw("");
    }
    setBusy(false);
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(18,14,8,0.88)", zIndex:950,
      display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:C.card,
        border:`2px solid ${C.border}`, borderRadius:3, padding:30, width:300, textAlign:"center" }}>
        <div style={{ fontSize:8, fontFamily:"monospace", fontWeight:700, color:C.muted, letterSpacing:3, marginBottom:8 }}>RUBENOLOGY™ ADMIN</div>
        <div style={{ fontSize:20, fontWeight:700, fontFamily:"monospace", color:C.text, marginBottom:4 }}>🔐 Enter</div>
        <div style={{ fontSize:11, color:C.muted, marginBottom:18, fontStyle:"italic" }}>For Ruben's eyes only.</div>
        <Inp type="email" value={email} onChange={e=>setEmail(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&attempt()} placeholder="email"
          style={{ textAlign:"center", fontFamily:"monospace", marginBottom:8, border:`1.5px solid ${err?C.rose:C.border}` }} />
        <Inp type="password" value={pw} onChange={e=>setPw(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&attempt()} placeholder="password"
          style={{ textAlign:"center", fontFamily:"monospace", border:`1.5px solid ${err?C.rose:C.border}` }} />
        {err && <div style={{ fontSize:10, color:C.rose, marginTop:5, fontFamily:"monospace" }}>Wrong email or password.</div>}
        <Btn onClick={attempt} disabled={busy} style={{ width:"100%", marginTop:12, background:C.rose, border:"none", color:"#fff", fontSize:11 }}>{busy?"…":"ENTER →"}</Btn>
      </div>
    </div>
  );
};

// ── Subscribe ─────────────────────────────────────────────────
const SubscribeBox = () => {
  const [email, setEmail] = useState(""); const [done, setDone] = useState(false); const [busy, setBusy] = useState(false);
  const subscribe = async () => {
    if (!email.includes("@") || busy) return;
    setBusy(true);
    try {
      await addDoc(collection(db,"subscribers"), { email:email.trim(), date:fmt(), subscribedAt:serverTimestamp() });
      setDone(true);
    } catch(e) { console.error(e); }
    setBusy(false);
  };
  return (
    <div style={{ background:C.white, border:`2px dashed ${C.border}`, borderRadius:3, padding:"20px 22px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
        <div style={{ width:4, height:32, background:C.mustard, flexShrink:0 }} />
        <div>
          <div style={{ fontSize:12, fontWeight:700, fontFamily:"monospace", color:C.text }}>Stay in the Rubenological loop</div>
          <div style={{ fontSize:12, color:C.muted }}>New posts to your inbox. No spam.</div>
        </div>
      </div>
      {done ? <div style={{ padding:"10px 14px", border:`1.5px solid ${C.sage}`, borderRadius:2, color:C.sage, fontFamily:"monospace", fontSize:12, fontWeight:700 }}>✓ Subscribed. Welcome to the field.</div>
        : <div style={{ display:"flex", gap:8 }}>
            <Inp type="email" value={email} onChange={e=>setEmail(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&subscribe()}
              placeholder="your@email.com" style={{ flex:1, fontSize:12, fontFamily:"monospace" }} />
            <Btn onClick={subscribe} style={{ background:C.mustard, border:"none", color:"#fff", whiteSpace:"nowrap" }}>
              {busy?"…":"SUBSCRIBE"}
            </Btn>
          </div>
      }
    </div>
  );
};

// ── Main App ──────────────────────────────────────────────────
export default function App() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState("home");
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);
  const [admin, setAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLogin, setLogin] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [dashboard, setDashboard] = useState(false);

  useEffect(() => {
    const pq = query(collection(db,"posts"), orderBy("createdAt","desc"));
    return onSnapshot(pq, snap => { setPosts(snap.docs.map(d=>({id:d.id,...d.data()}))); setLoading(false); });
  }, []);
  useEffect(() => onAuthStateChanged(auth, u => setIsAdmin(!!u)), []);

  const publish = async (form, editId) => {
    if(editId) await updateDoc(doc(db,"posts",editId), {...form});
    else await addDoc(collection(db,"posts"), {...form, likes:0, date:fmt(), createdAt:serverTimestamp()});
    setAdmin(false); setEditPost(null);
    if(!editId) setPage("posts");
  };

  const del = async id => {
    const s = await getDocs(collection(db,"posts",id,"comments"));
    await Promise.all(s.docs.map(d=>deleteDoc(d.ref)));
    await deleteDoc(doc(db,"posts",id));
    if(modal?.id===id) setModal(null);
  };

  const filtered = posts.filter(p => {
    const okCat = cat==="All" || p.cat===cat;
    const okQ = !q || [p.title,p.excerpt,p.cat].some(s=>(s||"").toLowerCase().includes(q.toLowerCase()));
    return okCat && okQ;
  });

  const NB = (id,label) => (
    <button key={id} onClick={()=>setPage(id)} style={{ background:"none", border:"none",
      cursor:"pointer", padding:"5px 13px", fontSize:10, fontWeight:700,
      fontFamily:"monospace", letterSpacing:2, color:page===id?C.text:C.muted,
      borderBottom:page===id?`2px solid ${C.rose}`:"2px solid transparent" }}>{label}</button>
  );

  return (
    <div style={{ background:C.bg, backgroundImage:`radial-gradient(circle,${C.border} 1px,transparent 1px)`,
      backgroundSize:"22px 22px", minHeight:"100vh", fontFamily:"Georgia,'Times New Roman',serif", color:C.text }}>

      {/* NAV */}
      <nav style={{ background:"rgba(240,233,214,0.95)", backdropFilter:"blur(8px)",
        borderBottom:`2px solid ${C.border}`, position:"sticky", top:0, zIndex:200 }}>
        <div style={{ maxWidth:920, margin:"0 auto", padding:"0 22px",
          display:"flex", alignItems:"center", justifyContent:"space-between", height:52, gap:12, flexWrap:"wrap" }}>
          <div onClick={()=>setPage("home")} style={{ cursor:"pointer", lineHeight:1 }}>
            <span style={{ fontSize:17, fontWeight:700, fontFamily:"monospace", letterSpacing:1, color:C.text }}>RUBENOLOGY</span>
            <sup style={{ fontSize:7, color:C.rose, fontFamily:"monospace", fontWeight:700 }}>™</sup>
          </div>
          <div style={{ display:"flex" }}>{NB("home","HOME")}{NB("posts","POSTS")}{NB("about","ABOUT")}</div>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <div style={{ position:"relative", display:"inline-flex", alignItems:"center" }}>
              <span style={{ position:"absolute", left:9, fontSize:13, color:C.muted, pointerEvents:"none" }}>⌕</span>
              <input value={q} onChange={e=>{setQ(e.target.value);if(e.target.value)setPage("posts");}}
                placeholder="Search…"
                style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:2,
                  padding:"6px 28px", fontSize:11, fontFamily:"monospace", color:C.text, outline:"none", width:160 }} />
              {q && <button onClick={()=>setQ("")} style={{ position:"absolute", right:8, background:"none",
                border:"none", cursor:"pointer", color:C.muted, fontSize:14, lineHeight:1, padding:0 }}>×</button>}
            </div>
            {isAdmin
              ? <div style={{ display:"flex", gap:6 }}>
                  <Btn onClick={()=>setDashboard(true)} style={{ background:"none", border:`1.5px solid ${C.mustard}`, color:C.mustard, padding:"6px 12px", fontSize:9 }}>📊 DASHBOARD</Btn>
                  <Btn onClick={()=>{setEditPost(null);setAdmin(true)}} style={{ background:C.rose, border:"none", color:"#fff", padding:"6px 12px", fontSize:9 }}>+ WRITE</Btn>
                  <Btn onClick={()=>signOut(auth)} style={{ background:"none", border:`1.5px solid ${C.border}`, color:C.muted, padding:"6px 10px", fontSize:9 }}>EXIT</Btn>
                </div>
              : <Btn onClick={()=>setLogin(true)} style={{ background:"none", border:`1.5px solid ${C.border}`, color:C.muted, padding:"6px 12px", fontSize:9 }}>ADMIN</Btn>
            }
          </div>
        </div>
      </nav>

      <div style={{ maxWidth:920, margin:"0 auto", padding:"0 22px 72px" }}>

        {page==="home" && <>
          <div style={{ padding:"48px 0 36px", borderBottom:`1px dashed ${C.light}`, marginBottom:32 }}>
            <div style={{ fontSize:9, fontFamily:"monospace", fontWeight:700, color:C.rose, letterSpacing:4, marginBottom:12 }}>EST. NOW · UPDATED REGULARLY</div>
            <div style={{ display:"flex", gap:20, flexWrap:"wrap", alignItems:"flex-start" }}>
              <div style={{ flex:"1 1 280px" }}>
                <h1 style={{ fontSize:"clamp(28px,5vw,50px)", fontWeight:700, fontFamily:"monospace", lineHeight:1, margin:"0 0 6px", letterSpacing:-1, color:C.text }}>
                  RUBENOLOGY<sup style={{ fontSize:"35%", color:C.rose, verticalAlign:"super" }}>™</sup>
                </h1>
                <div style={{ height:3, width:48, background:C.rose, margin:"10px 0 14px" }} />
                <p style={{ fontSize:13, color:C.muted, fontStyle:"italic", lineHeight:1.8, maxWidth:420, margin:"0 0 22px" }}>
                  "The unofficial academic field dedicated to the study of everything and nothing, as observed by one Ruben."
                </p>
                <div style={{ display:"flex", gap:10 }}>
                  <Btn onClick={()=>setPage("posts")} style={{ background:C.rose, border:"none", color:"#fff" }}>EXPLORE →</Btn>
                  <Btn onClick={()=>setPage("about")} style={{ background:"none", border:`2px solid ${C.border}`, color:C.muted }}>ABOUT</Btn>
                </div>
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {Object.entries(CATS).map(([cat,m])=>(
                  <div key={cat} onClick={()=>{setCat(cat);setPage("posts")}}
                    style={{ background:C.white, border:`2px solid ${C.border}`, borderRadius:2, padding:"12px 14px", cursor:"pointer", textAlign:"center", minWidth:76 }}>
                    <div style={{ fontSize:20, color:m.color, marginBottom:5 }}>{m.icon}</div>
                    <div style={{ fontSize:8, fontFamily:"monospace", fontWeight:700, color:C.muted, letterSpacing:1 }}>{cat.toUpperCase()}</div>
                    <div style={{ fontSize:11, fontFamily:"monospace", color:m.color, fontWeight:700, marginTop:2 }}>{posts.filter(p=>p.cat===cat).length}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <div style={{ fontSize:9, fontFamily:"monospace", fontWeight:700, color:C.muted, letterSpacing:3 }}>LATEST DISPATCHES</div>
            <button onClick={()=>setPage("posts")} style={{ fontSize:9, fontFamily:"monospace", color:C.rose, background:"none", border:"none", cursor:"pointer", fontWeight:700 }}>ALL POSTS →</button>
          </div>
          {loading ? <div style={{ fontFamily:"monospace", fontSize:12, color:C.muted, padding:"32px 0" }}>Loading…</div>
            : <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))", gap:12, marginBottom:36 }}>
                {posts.slice(0,3).map(p=><PostCard key={p.id} post={p} onClick={setModal} isAdmin={isAdmin} onEdit={e=>{setEditPost(e);setAdmin(true)}} onDelete={del} />)}
              </div>
          }
          <SubscribeBox />
        </>}

        {page==="posts" && (
          <div style={{ padding:"36px 0" }}>
            <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:8 }}>
              <div>
                <div style={{ fontSize:8, fontFamily:"monospace", color:C.muted, letterSpacing:3, marginBottom:4 }}>ARCHIVE</div>
                <div style={{ fontSize:24, fontWeight:700, fontFamily:"monospace" }}>
                  All Posts <span style={{ fontSize:13, color:C.muted, fontWeight:400 }}>({filtered.length})</span>
                </div>
              </div>
              {q && <div style={{ fontSize:11, fontFamily:"monospace", color:C.rose, fontStyle:"italic" }}>"{q}"</div>}
            </div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:22 }}>
              {ALL_CATS.map(c=>(
                <Btn key={c} onClick={()=>setCat(c)} style={{ padding:"5px 12px",
                  background:cat===c?C.rose:"none", border:`1.5px solid ${cat===c?C.rose:C.border}`,
                  color:cat===c?"#fff":C.muted, fontSize:9 }}>
                  {c==="All"?"★ ALL":c.toUpperCase()}
                </Btn>
              ))}
            </div>
            {loading ? <div style={{ fontFamily:"monospace", fontSize:12, color:C.muted, padding:"32px 0" }}>Loading…</div>
              : filtered.length===0
                ? <div style={{ textAlign:"center", padding:"52px 0" }}>
                    <div style={{ fontSize:28, color:C.muted, marginBottom:8 }}>◈</div>
                    <div style={{ fontFamily:"monospace", fontSize:12, color:C.muted }}>{q?"Nothing matches.":"Nothing here yet."}</div>
                  </div>
                : <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))", gap:12 }}>
                    {filtered.map(p=><PostCard key={p.id} post={p} onClick={setModal} isAdmin={isAdmin} onEdit={e=>{setEditPost(e);setAdmin(true)}} onDelete={del} />)}
                  </div>
            }
            <div style={{ marginTop:36 }}><SubscribeBox /></div>
          </div>
        )}

        {page==="about" && (
          <div style={{ padding:"48px 0", maxWidth:580 }}>
            <div style={{ fontSize:8, fontFamily:"monospace", color:C.mustard, letterSpacing:3, marginBottom:8 }}>ABOUT</div>
            <h2 style={{ fontSize:26, fontWeight:700, fontFamily:"monospace", margin:"0 0 6px" }}>What is Rubenology?</h2>
            <div style={{ height:2, width:36, background:C.mustard, marginBottom:22 }} />
            <p style={{ fontSize:14, color:C.muted, lineHeight:1.9, marginBottom:18 }}>Rubenology™ is the self-founded, peer-unreviewed academic discipline of being Ruben. It encompasses six core fields of inquiry:</p>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:28 }}>
              {Object.entries(CATS).map(([cat,m])=>(
                <div key={cat} style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 16px", background:C.card, border:`2px solid ${C.border}`, borderRadius:2 }}>
                  <span style={{ fontSize:20, color:m.color, minWidth:24, textAlign:"center" }}>{m.icon}</span>
                  <div>
                    <div style={{ fontSize:10, fontWeight:700, fontFamily:"monospace", color:m.color, letterSpacing:1, marginBottom:3 }}>{cat.toUpperCase()}</div>
                    <div style={{ fontSize:12, color:C.muted }}>{{Writing:"Essays, thoughts, and the occasional manifesto.",Photography:"Visual dispatches from the observable world.",Sports:"Athletic endeavours, victories, and spectacular defeats.",Music:"Playlists, discoveries, and notes on sound.","App Dev":"Things Ruben built, broke, and rebuilt again.",YouTube:"Video logs, uploads, and things Ruben filmed."}[cat]}</div>
                  </div>
                </div>
              ))}
            </div>
            <SubscribeBox />
          </div>
        )}
      </div>

      {modal && <PostModal post={modal} onClose={()=>setModal(null)} isAdmin={isAdmin} />}
      {admin && <AdminPanel onClose={()=>{setAdmin(false);setEditPost(null)}} onSave={publish} editPost={editPost} />}
      {showLogin && <LoginModal onClose={()=>setLogin(false)} />}
      {dashboard && <Dashboard onClose={()=>setDashboard(false)} posts={posts} />}
    </div>
  );
}